import FormSelectDropdownField from "shared/form/fields/selectDropdown";
import React from "react";
import { FieldArray, useField, useFormikContext } from "formik";

import Button from "shared/components/button";
import { ApiEventParticipant } from "shared/types/ApiEventParticipant";
import { ApiEventParticipantForContactContext } from "shared/types/contact/ApiEventParticipantForContactContext";
import { useCopies } from "shared/hooks/useCopies";
import ToolTip from "shared/components/tooltip";
import Icon from "assets/icons";
import ErrorField from "shared/form/fields/errorField";
import FormLabel from "shared/form/fields/formLabel";
import MaterialTable from "shared/components/materialTable";
import { MaterialTableColumn } from "shared/components/materialTable/types";
import { LicenseCap } from "./LicenseCap";
import { AddParticipantInContextMyExperienceProps, RemoveParticipantInContextProps } from "shared/contactContext/types";
import { getCommonColumnsForContactContext, getParticipantsFromParticipantIds } from "shared/contactContext/utils";
import MessageBar from "shared/components/messageBar";
import { MessageType } from "shared/enums/MessageType";

export function AddParticipantInContextMyExperience(props: AddParticipantInContextMyExperienceProps) {
  const { setFieldValue, values, setFieldTouched } = useFormikContext<any>();
  const [, meta] = useField({
    name: props.name
  });
  const [map, setMap] = React.useState<Record<string, ApiEventParticipantForContactContext | ApiEventParticipant>>(
    props.participantMap
  );
  const copies = useCopies();
  const adminOrganizationName = props.participantMap[props.requesterId || ""]?.eventOrganization;
  const helper = React.useMemo(() => {
    return {
      licenseCapExceeded: !props.unlimitedLicenseCap && props.currentParticipantIds.length >= +(props.licenseCap || 0),
      admins:
        props.participants
          ?.filter(p => !props.currentParticipantIds.includes(p.id))
          .map(item => ({
            label: `${item.firstName} ${item.lastName} ${item.jobTitle ? `(${item.jobTitle})` : ""}`,
            value: item.id
          })) || [],
      licensees:
        props.participants
          ?.filter(p => !props.currentParticipantIds.includes(p.id))
          .map(item => ({
            label: `${item.firstName} ${item.lastName} ${item.jobTitle ? `(${item.jobTitle})` : ""}`,
            value: item.id
          })) || []
    };
  }, [props.currentParticipantIds, props.licenseCap, props.participants, props.unlimitedLicenseCap]);

  const allAssigned = React.useMemo(() => {
    return props.currentParticipantIds.length > 0 && helper[props.name].length === 0;
  }, [props.currentParticipantIds.length, props.name, helper]);

  return (
    <FieldArray name={props.name}>
      {({ push, remove }) => {
        return (
          <div className="addViewEntriesCombo">
            <div className="addViewEntriesCombo_title">
              <FormLabel label={copies.get(props.label)} />
              {!!props.showLicenseCap && (
                <LicenseCap
                  licenseCap={props.licenseCap}
                  unlimitedLicenseCap={props.unlimitedLicenseCap}
                  assigned={props.assigned}
                />
              )}
            </div>
            <div className="addViewEntriesCombo_wrapper">
              <div className="addViewEntriesCombo_addEntries">
                <FormSelectDropdownField
                  name={`${props.name}_add_new`}
                  label=""
                  options={helper[props.name]}
                  disabled={allAssigned || props.disabled}
                  placeholder={allAssigned ? copies.get("all.assigned") : copies.get("select")}
                />
                <Button.Add
                  className="addViewEntriesCombo_addEntries--btn"
                  disabled={!values[`${props.name}_add_new`] || props.disabled || allAssigned}
                  onClick={() => {
                    const p = props.participants.find(p => p.id === values[`${props.name}_add_new`]);
                    if (p) {
                      setMap({ ...map, [p.id]: p });
                    }
                    const newId = values[`${props.name}_add_new`];
                    push(newId);
                    setFieldTouched(props.name, true);
                    setFieldValue(`${props.name}_add_new`, "");
                  }}
                  isOutline
                />
              </div>
              <div className="addViewEntriesCombo_viewEntries">
                {props.tableNote && (
                  <div className="addViewEntriesCombo_tableNote">
                    <MessageBar type={MessageType.INFO}>{props.tableNote}</MessageBar>
                  </div>
                )}
                <MyExpTable
                  disabled={props.disabled}
                  data={getParticipantsFromParticipantIds(map, props.currentParticipantIds)}
                  currentParticipantIds={props.currentParticipantIds}
                  removeHandler={remove}
                  adminOrganizationName={adminOrganizationName}
                  participantMap={props.participantMap}
                  canRemoveSelf={props.canRemoveSelf}
                  requesterId={props.requesterId}
                  allowRemoveFromOtherOrgs={props.allowRemoveFromOtherOrgs}
                />
              </div>
              <ErrorField meta={meta} />
            </div>
          </div>
        );
      }}
    </FieldArray>
  );
}

function MyExpTable(props: {
  data: Array<ApiEventParticipant | ApiEventParticipantForContactContext>;
  currentParticipantIds: Array<string>;
  removeHandler: (index: number) => void;
  disabled: boolean;
  canRemoveSelf?: boolean;
  requesterId?: string;
  adminOrganizationName?: string;
  participantMap?: Record<string, ApiEventParticipantForContactContext>;
  allowRemoveFromOtherOrgs?: boolean;
}) {
  const copies = useCopies();
  const columns = React.useMemo(() => {
    const columnList: Array<MaterialTableColumn<ApiEventParticipant | ApiEventParticipantForContactContext>> = [
      ...getCommonColumnsForContactContext(copies, props.requesterId || ""),
      {
        name: "Action",
        width: 7,
        Cell: participant => (
          <RemoveParticipantInContext
            disabled={props.disabled}
            currentParticipantIds={props.currentParticipantIds}
            canRemoveSelf={!!props.canRemoveSelf}
            isSelf={participant.id === props.requesterId}
            fromSameOrganization={props.adminOrganizationName === participant.eventOrganization}
            remove={props.removeHandler}
            isNewlyAdded={!!props.participantMap?.[participant.id]}
            participant={participant}
            allowRemoveFromOtherOrgs={props.allowRemoveFromOtherOrgs}
          />
        )
      }
    ];
    return columnList;
  }, [props, copies]);
  return (
    <div className="addViewEntriesCombo_viewEntriesWrapper">
      <MaterialTable<ApiEventParticipant | ApiEventParticipantForContactContext>
        columns={columns}
        data={props.data}
        hideShadow
        showInlineNoResult
      />
    </div>
  );
}

function RemoveParticipantInContext(props: RemoveParticipantInContextProps) {
  const copies = useCopies();

  const removeButton = (
    <Button.Delete
      disabled={props.disabled}
      className="leadParticipantCanRemove"
      label={""}
      icon={<Icon.RemoveCircle size={19} />}
      onClick={e => {
        e.preventDefault();
        const indexToRemove = props.currentParticipantIds.indexOf(props.participant.id);
        props.remove(indexToRemove);
      }}
    />
  );
  if (props.isSelf) {
    if (!props.canRemoveSelf) {
      return (
        <ToolTip content={copies.get("contact.context.cannot.remove.self")} className="leadParticipantNotRemove">
          <Icon.InfoCircle size={16} />
        </ToolTip>
      );
    }
    return removeButton;
  }
  if (props.fromSameOrganization || props.allowRemoveFromOtherOrgs) {
    return removeButton;
  }

  return (
    <ToolTip content={copies.get("contact.context.cannot.remove.other.org.admin")} className="leadParticipantNotRemove">
      <Icon.InfoCircle size={16} />
    </ToolTip>
  );
}
